import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import { action, computed, isArrayLike, observable, runInAction, toJS } from 'mobx';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import { Size } from 'choerodon-ui/lib/_util/enum';
import { getConfig } from 'choerodon-ui/lib/configure';
import Icon from '../icon';
import { open } from '../modal-container/ModalContainer';
import LovView from './LovView';
import DataSet from '../data-set/DataSet';
import Record from '../data-set/Record';
import Spin from '../spin';
import lovStore from '../stores/LovCodeStore';
import autobind from '../_util/autobind';
import { stopEvent } from '../_util/EventManager';
import { isSearchTextEmpty, Select } from '../select/Select';
import { CheckedStrategy, DataSetStatus, RecordStatus } from '../data-set/enum';
import { SearchAction, ViewMode } from './enum';
import Button from '../button/Button';
import { ButtonColor, FuncType } from '../button/enum';
import { $l } from '../locale-context';
import { getLovPara } from '../stores/utils';
import isIE from '../_util/isIE';
let Lov = class Lov extends Select {
    constructor() {
        super(...arguments);
        this.openModal = action((fetchSingle) => {
            this.collapse();
            const { viewMode, onBeforeSelect } = this.props;
            if (viewMode === 'modal') {
                const config = this.getConfig();
                this.autoCreate();
                const { options } = this;
                if (!this.modal && config && options) {
                    const modalProps = this.getModalProps();
                    const tableProps = this.getTableProps();
                    const { width, title } = config;
                    const lovViewProps = this.beforeOpen(options);
                    this.modal = open({
                        title,
                        children: (React.createElement(LovView, Object.assign({}, lovViewProps, { dataSet: options, config: config, tableProps: { ...(lovViewProps && lovViewProps.tableProps), ...tableProps }, onSelect: this.handleLovViewSelect, onBeforeSelect: onBeforeSelect, multiple: this.multiple, values: this.getValues() }))),
                        onClose: this.handleLovViewClose,
                        destroyOnClose: true,
                        closable: true,
                        autoFocus: false,
                        bodyStyle: {
                            minHeight: isIE() ? pxToRem(Math.min(350, window.innerHeight)) : 'min(3.5rem, 100vh)',
                        },
                        ...modalProps,
                        style: {
                            width: pxToRem(width),
                            ...(modalProps && modalProps.style),
                        },
                        afterClose: this.handleLovViewAfterClose,
                    });
                    this.afterOpen(options, fetchSingle);
                }
            }
        });
    }
    get searchMatcher() {
        const { searchMatcher } = this.observableProps;
        if (isString(searchMatcher)) {
            return searchMatcher;
        }
        return this.textField;
    }
    get searchable() {
        const { searchable } = this.observableProps;
        if (searchable === false) {
            return searchable;
        }
        const config = this.getConfig();
        if (config) {
            return config.editableFlag === 'Y';
        }
        return true;
    }
    get lovCode() {
        const { field } = this;
        if (field) {
            return field.get('lovCode');
        }
        return undefined;
    }
    get popup() {
        return this.modal || (!this.isSearchFieldInPopup() && !this.searchText) ? false : this.statePopup;
    }
    /**
     * 点击查询仅存在一条数据时自动选中
     */
    get autoSelectSingle() {
        if ('autoSelectSingle' in this.props) {
            return this.props.autoSelectSingle;
        }
        const autoSelectSingle = getConfig('lovAutoSelectSingle');
        if (typeof autoSelectSingle !== 'undefined') {
            return autoSelectSingle;
        }
        return false;
    }
    get options() {
        const { field, lovCode } = this;
        if (field) {
            const { options } = field;
            if (options) {
                return options;
            }
        }
        if (lovCode) {
            const lovDataSet = lovStore.getLovDataSet(lovCode, field, field && field.get('optionsProps'));
            if (lovDataSet) {
                return lovDataSet;
            }
        }
        return new DataSet();
    }
    getSearchFieldProps() {
        const searchFieldProps = super.getSearchFieldProps();
        const { viewMode } = this.props;
        if (viewMode === 'popup') {
            return {
                multiple: true,
                ...searchFieldProps,
            };
        }
        return searchFieldProps;
    }
    isSearchFieldInPopup() {
        const searchFieldInPopup = super.isSearchFieldInPopup();
        if (searchFieldInPopup === undefined) {
            const { viewMode } = this.props;
            return viewMode === 'popup';
        }
        return searchFieldInPopup;
    }
    isEditable() {
        const { viewMode } = this.props;
        return viewMode !== 'popup' && super.isEditable();
    }
    getPopupLovView() {
        const config = this.getConfig();
        this.autoCreate();
        const { options } = this;
        if (config && options) {
            let lovViewProps;
            if (this.popup && !this.fetched) {
                runInAction(() => {
                    lovViewProps = this.beforeOpen(options);
                    this.afterOpen(options);
                    this.fetched = true;
                });
            }
            const tableProps = this.getTableProps();
            const mergedTableProps = {
                ...(lovViewProps && lovViewProps.tableProps),
                ...tableProps,
                style: {
                    ...tableProps.style,
                    maxHeight: 250,
                },
                pagination: { showSizeChanger: false },
                queryBar: this.renderSearchField,
                border: false,
                size: Size.small,
            };
            const { onBeforeSelect } = this.props;
            return (React.createElement(LovView, Object.assign({}, lovViewProps, { popup: true, dataSet: options, config: config, tableProps: mergedTableProps, onSelect: this.handleLovViewSelect, onBeforeSelect: onBeforeSelect, multiple: this.multiple, values: this.getValues(), popupHidden: !this.popup })));
        }
    }
    getPopupContent() {
        const { searchAction, viewMode } = this.props;
        if (viewMode === 'popup') {
            return this.getPopupLovView();
        }
        if (searchAction === SearchAction.input) {
            return super.getPopupContent();
        }
        return null;
    }
    beforeOpen(options) {
        const { multiple, primitive, valueField } = this;
        if (multiple) {
            options.selectionStrategy = this.getProp('showCheckedStrategy') || CheckedStrategy.SHOW_ALL;
        }
        const { viewMode } = this.props;
        const { selected } = options;
        if (viewMode === 'modal') {
            options.unSelectAll();
            // TODO：lovEvents deprecated
            const { lovEvents } = this.props;
            if (lovEvents) {
                Object.keys(lovEvents).forEach(event => options.addEventListener(event, lovEvents[event]));
            }
        }
        if (multiple) {
            const needToFetch = new Map();
            options.clearCachedSelected();
            options.setCachedSelected(this.getValues().map(value => {
                const primitiveValue = primitive ? value : value[valueField];
                const oldSelected = selected.find(r => r.get(valueField) === primitiveValue);
                if (oldSelected) {
                    oldSelected.isSelected = true;
                    return oldSelected;
                }
                const newSelected = new Record(primitive ? { [valueField]: value } : toJS(value), options, RecordStatus.sync);
                newSelected.isSelected = true;
                newSelected.isCached = true;
                needToFetch.set(primitiveValue, newSelected);
                return newSelected;
            }));
            const { lovCode } = this;
            if (lovCode) {
                const lovQueryCachedSelected = getConfig('lovQueryCachedSelected');
                if (lovQueryCachedSelected) {
                    const fetchCachedSelected = () => {
                        if (needToFetch.size) {
                            lovQueryCachedSelected(lovCode, needToFetch).then(action((results) => {
                                results.forEach((data) => {
                                    const record = needToFetch.get(data[valueField]);
                                    if (record) {
                                        record.init(data);
                                    }
                                });
                                needToFetch.clear();
                            }));
                        }
                    };
                    if (viewMode === 'popup') {
                        fetchCachedSelected();
                    }
                    else {
                        return {
                            tableProps: {
                                onShowCachedSelectionChange: (visible) => {
                                    if (visible) {
                                        fetchCachedSelected();
                                    }
                                },
                            },
                        };
                    }
                }
            }
        }
    }
    afterOpen(options, fetchSingle) {
        const noCache = this.getProp('noCache');
        if (this.resetOptions(noCache) && fetchSingle !== true && !this.multiple) {
            options.query();
        }
        else if (this.multiple) {
            if (this.resetOptions(noCache)) {
                options.query();
            }
            options.releaseCachedSelected();
        }
    }
    setText(text) {
        this.searching = true;
        super.setText(text);
    }
    /**
     * 处理 Lov input 查询参数
     * @param text
     */
    searchRemote(text) {
        const { options, searchMatcher } = this;
        if (isString(searchMatcher) && !isSearchTextEmpty(text)) {
            this.resetOptions(true);
            const searchPara = this.getSearchPara(searchMatcher, text);
            Object.keys(searchPara).forEach(key => {
                const value = searchPara[key];
                options.setQueryParameter(key, value === '' ? undefined : value);
            });
            if (this.isSearchFieldInPopup() || this.props.searchAction === SearchAction.input) {
                options.query().then(() => delete this.searching);
            }
        }
    }
    handlePopupHiddenChange(hidden) {
        super.handlePopupHiddenChange(hidden);
        if (hidden) {
            delete this.fetched;
        }
    }
    handleLovViewClose() {
        this.focus();
    }
    /**
     * 关闭弹窗移除时间监听 后续废弃
     */
    handleLovViewAfterClose() {
        // TODO：lovEvents deprecated
        const { options, props: { lovEvents } } = this;
        const { afterClose = noop } = this.getModalProps();
        afterClose();
        if (lovEvents) {
            Object.keys(lovEvents).forEach(event => options.removeEventListener(event, lovEvents[event]));
        }
        this.setPopup(false);
        this.modal = undefined;
    }
    handleLovViewSelect(records) {
        const { viewMode } = this.props;
        if (viewMode === 'popup' && !this.multiple) {
            this.collapse();
        }
        if (isArrayLike(records)) {
            this.setValue(records.map(record => this.processRecordToObject(record)));
        }
        else {
            this.setValue(records && this.processRecordToObject(records) || this.emptyValue);
        }
    }
    resetOptions(noCache = false) {
        const { field, record, options } = this;
        const { queryDataSet, props: { pageSize } } = options;
        let dirty = noCache;
        if (noCache) {
            options.pageSize = pageSize || 10;
        }
        if (queryDataSet && noCache) {
            const { current } = queryDataSet;
            if (current && current.dirty) {
                dirty = true;
                current.reset();
            }
        }
        if (field) {
            const lovPara = getLovPara(field, record);
            if (!isEqual(lovPara, options.queryParameter)) {
                options.queryParameter = lovPara;
                return true;
            }
            options.first();
            if (!options.length) {
                return true;
            }
        }
        return dirty;
    }
    handleKeyDown(e) {
        if (!this.popup && e.keyCode === KeyCode.DOWN) {
            stopEvent(e);
            this.openModal();
        }
        if (e.keyCode === KeyCode.ENTER && this.props.searchAction === SearchAction.blur) {
            stopEvent(e);
            this.blur();
        }
        if (!(e.keyCode === KeyCode.ENTER && this.searching)) {
            super.handleKeyDown(e);
        }
    }
    handleBlur(e) {
        if (this.modal) {
            e.preventDefault();
        }
        super.handleBlur(e);
    }
    getWrapperProps() {
        return super.getWrapperProps({
            onDoubleClick: (this.disabled || this.readOnly) ? undefined : this.handleOpenModal,
            // Support ued to distinguish between select and lov
            className: this.getWrapperClassNames(`${this.prefixCls}-lov`),
        });
    }
    getPopupClassName(defaultClassName) {
        const { viewMode } = this.props;
        return classNames(defaultClassName, { [`${this.prefixCls}-lov-popup`]: viewMode === 'popup' });
    }
    syncValueOnBlur(value) {
        const { textField } = this;
        const { mode, searchAction, fetchSingle } = this.props;
        if (mode !== ViewMode.button) {
            let hasRecord = false;
            if (this.getValue()) {
                hasRecord = this.getValue()[textField] === value;
            }
            if (searchAction === SearchAction.blur && value && !hasRecord) {
                const { options } = this;
                options.query().then(() => {
                    const { length } = options;
                    if ((length > 1 && !fetchSingle) || length === 1) {
                        this.choose(options.get(0));
                    }
                    else if (length && fetchSingle) {
                        this.openModal(fetchSingle);
                    }
                });
            }
            else {
                super.syncValueOnBlur(value);
            }
        }
    }
    getConfig() {
        const { lovCode } = this;
        if (lovCode) {
            return lovStore.getConfig(lovCode);
        }
    }
    getPlaceholders() {
        const placeholder = super.getPlaceholders();
        if (placeholder.length) {
            return placeholder;
        }
        const config = this.getConfig();
        if (config) {
            const { placeholder: holder } = config;
            const holders = [];
            return holder ? holders.concat(holder) : holders;
        }
        return [];
    }
    getModalProps() {
        const { modalProps } = this.props;
        return { ...getConfig('lovModalProps'), ...modalProps };
    }
    getTableProps() {
        const { tableProps } = this.props;
        return { ...getConfig('lovTableProps'), ...tableProps };
    }
    selectSingle() {
        const { options } = this;
        this.resetOptions(options.length === 1);
        options.query().then(() => {
            if (options.length === 1) {
                this.choose(this.options.get(0));
            }
            else {
                this.openModal();
            }
        });
    }
    handleOpenModal() {
        if (!this.disabled && !this.readOnly) {
            return this.autoSelectSingle ? this.selectSingle() : this.openModal();
        }
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'modalProps',
            'noCache',
            'tableProps',
            'lovEvents',
            'searchAction',
            'fetchSingle',
            'autoSelectSingle',
            'showCheckedStrategy',
            'onBeforeSelect',
        ]);
    }
    getButtonProps() {
        const { className, type } = this.props;
        const { options } = this;
        const props = {
            ...Button.defaultProps,
            ...omit(this.getOtherProps(), ['name']),
            dataSet: options,
            className: classNames(className, `${this.prefixCls}-lov`),
            type,
        };
        if (!this.isValid) {
            props.color = ButtonColor.red;
        }
        return props;
    }
    get loading() {
        const { options, props: { searchAction } } = this;
        return (searchAction === SearchAction.blur || Boolean(this.autoSelectSingle)) && options.status === DataSetStatus.loading && !this.popup;
    }
    getSuffix() {
        const { suffix, viewMode } = this.props;
        if (viewMode === 'popup') {
            return super.getSuffix();
        }
        const icon = this.loading ? React.createElement(Spin, { className: `${this.prefixCls}-lov-spin` }) : React.createElement(Icon, { type: "search" });
        return this.wrapperSuffix(suffix || icon, {
            onClick: (this.disabled || this.readOnly) ? undefined : this.handleOpenModal,
        });
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        if (this.modal) {
            this.modal.close();
        }
    }
    select() {
        const { mode } = this.props;
        if (mode !== ViewMode.button) {
            super.select();
        }
    }
    handleButtonClick(e) {
        this.handleOpenModal();
        const { onClick = noop } = this.props;
        onClick(e);
    }
    renderWrapper() {
        const { mode, children, clearButton } = this.props;
        if (mode === ViewMode.button) {
            const elements = [
                React.createElement(Button, Object.assign({ key: "lov_button" }, this.getButtonProps(), { disabled: this.disabled, onClick: this.handleButtonClick }), children || this.getTextNode() || this.getPlaceholders()[0] || $l('Lov', 'choose')),
            ];
            if (clearButton) {
                elements.push(React.createElement(Button, { key: "lov_clear_button", size: Size.small, funcType: FuncType.flat, icon: "close", onClick: this.handleClearButtonClick }));
            }
            return elements;
        }
        return super.renderWrapper();
    }
};
Lov.displayName = 'Lov';
Lov.propTypes = {
    ...Select.propTypes,
    ...Button.propTypes,
    modalProps: PropTypes.object,
    tableProps: PropTypes.object,
    noCache: PropTypes.bool,
    fetchSingle: PropTypes.bool,
    autoSelectSingle: PropTypes.bool,
    /**
     * 触发查询变更的动作， default: input
     */
    searchAction: PropTypes.oneOf([SearchAction.blur, SearchAction.input]),
    showCheckedStrategy: PropTypes.string,
};
Lov.defaultProps = {
    ...Select.defaultProps,
    clearButton: true,
    checkValueOnOptionsChange: false,
    dropdownMatchSelectWidth: false,
    searchAction: SearchAction.input,
    fetchSingle: false,
    viewMode: 'modal',
};
__decorate([
    observable
], Lov.prototype, "modal", void 0);
__decorate([
    computed
], Lov.prototype, "searchMatcher", null);
__decorate([
    computed
], Lov.prototype, "searchable", null);
__decorate([
    computed
], Lov.prototype, "lovCode", null);
__decorate([
    computed
], Lov.prototype, "options", null);
__decorate([
    autobind
], Lov.prototype, "getPopupContent", null);
__decorate([
    action
], Lov.prototype, "beforeOpen", null);
__decorate([
    action
], Lov.prototype, "afterOpen", null);
__decorate([
    action
], Lov.prototype, "setText", null);
__decorate([
    action
], Lov.prototype, "searchRemote", null);
__decorate([
    autobind
], Lov.prototype, "handlePopupHiddenChange", null);
__decorate([
    autobind
], Lov.prototype, "handleLovViewClose", null);
__decorate([
    autobind,
    action
], Lov.prototype, "handleLovViewAfterClose", null);
__decorate([
    autobind
], Lov.prototype, "handleLovViewSelect", null);
__decorate([
    autobind
], Lov.prototype, "handleKeyDown", null);
__decorate([
    autobind
], Lov.prototype, "handleBlur", null);
__decorate([
    autobind,
    action
], Lov.prototype, "selectSingle", null);
__decorate([
    autobind
], Lov.prototype, "handleOpenModal", null);
__decorate([
    computed
], Lov.prototype, "loading", null);
__decorate([
    autobind
], Lov.prototype, "handleButtonClick", null);
Lov = __decorate([
    observer
], Lov);
export default Lov;
